import { mixins, tokens } from 'src/design-system';

/**
 * Additional Vendor Charges — Part 1 declaration styles (ISS-291748 / C1).
 *
 * The field renders full-width inside the task detail modal (it is registered in
 * `SEFieldGroup`'s `tasksToShowInVerticalView`), so it lays its own two-column grid out rather than
 * inheriting the modal's `1fr 2fr 1fr 2fr` field grid.
 */
const styles = () => ({
  /**
   * Tight vertical rhythm: the single header row sits just above the first control row (form-field
   * spacing), not the wide 24px that made the labels look detached from their inputs.
   */
  container: {
    ...mixins.layout.flexColumn(),
    gap: `${tokens.spacing.sm}px`,
    width: '100%',
  },

  /**
   * One grid template shared by the header row and every data row, so the single "Additional charge" /
   * "Type of charge" headers line up with the controls beneath them. Explicit px `gap` (the mixin's
   * numeric gap is dropped by this JSS build) and `center` so the × sits level with the controls.
   */
  row: {
    ...mixins.layout.gridLayout('1fr 1fr'),
    gap: `${tokens.spacing.lg}px`,
    alignItems: 'center',
  },

  label: {
    ...mixins.typography.small,
    color: tokens.colors.textSecondary,
  },

  select: {
    width: '100%',
  },

  /**
   * Type of charge is never an input. It is rendered as a disabled control so it reads as a value
   * that belongs to the chosen charge rather than something the vendor can fill in.
   */
  readOnlyValue: {
    '& .ant-input[disabled]': {
      color: tokens.colors.text,
      backgroundColor: tokens.colors.bgSubtle,
      cursor: 'default',
    },
  },

  /** Red × per the mockup — an unambiguous "remove this row", not a muted trash affordance. */
  removeIcon: {
    color: tokens.colors.error,
    cursor: 'pointer',
    fontSize: tokens.fontSize.h3,
    '&:hover': {
      opacity: 0.7,
    },
  },

  rowWithRemove: {
    ...mixins.layout.gridLayout('1fr 1fr auto'),
    gap: `${tokens.spacing.lg}px`,
    alignItems: 'center',
  },

  addRow: {
    ...mixins.layout.flexRow(undefined, 'center'),
    gap: `${tokens.spacing.xs}px`,
    ...mixins.interactive.linkStyle,
    marginTop: tokens.spacing.xs,
    width: 'fit-content',
  },

  /**
   * Cancel + Submit, right-aligned. Explicit px `gap` (not the mixin's numeric gap) so the space
   * between the two buttons renders regardless of the JSS unit plugin — the buttons were touching.
   */
  nextRow: {
    ...mixins.layout.flexRow(undefined, 'center', 'flex-end'),
    gap: `${tokens.spacing.sm}px`,
    marginTop: tokens.spacing.md,
  },

  message: {
    ...mixins.typography.small,
    color: tokens.colors.textTertiary,
  },

  errorMessage: {
    ...mixins.typography.small,
    color: tokens.colors.error,
  },

  spinner: {
    ...mixins.layout.flexCenter,
    padding: tokens.spacing.lg,
  },

  /** Read-only summary shown when the task is no longer editable. */
  summaryRow: {
    ...mixins.layout.gridLayout('1fr 1fr'),
    gap: `${tokens.spacing.lg}px`,
    padding: `${tokens.spacing.xs}px 0`,
  },

  summaryValue: {
    ...mixins.typography.body,
    color: tokens.colors.text,
    fontWeight: tokens.fontWeight.semibold,
  },
});

export default styles;
